import api from '../utils/apiClient';

// ⭐ Capa de servicios de la operación MARKETOS (spec §12).
//
// Regla: NINGÚN componente visual llama a axios ni conoce una ruta de la
// API. Todo pasa por acá. Esto vale por tres cosas concretas:
//
//  1. Si mañana el backend renombra /api/agency/requests, se cambia en UN
//     archivo y no en doce pantallas.
//  2. Los errores del backend (que vienen en inglés, con mensajes de base de
//     datos, o directamente sin mensaje) se traducen UNA vez a algo que un
//     cliente no técnico pueda leer — spec §11 prohíbe expresamente mostrar
//     mensajes de error internos.
//  3. Las etiquetas de estado y de tipo de cambio viven junto a las llamadas,
//     así admin y portal no pueden discrepar sobre cómo se llama "returned".
//
// Contrapartida asumida: este archivo crece. Es preferible a que crezca el
// acoplamiento entre pantallas y backend.

// ───────────────────────────────────────────────────────────────────────────
// Errores
// ───────────────────────────────────────────────────────────────────────────

// Motivos de fallo que la UI sabe representar de forma distinta. Todo lo que
// no encaje se degrada a 'error' genérico, nunca se muestra el mensaje crudo.
export const FAILURE = {
  NETWORK: 'network',
  UNAUTHORIZED: 'unauthorized',   // 401 — sesión expirada
  FORBIDDEN: 'forbidden',         // 403 — sin permisos
  NOT_FOUND: 'not_found',
  CONFLICT: 'conflict',           // 409 — la regla de negocio lo impide
  VALIDATION: 'validation',       // 400/422 — falta algo en el formulario
  SERVER: 'server'
};

const FRIENDLY = {
  [FAILURE.NETWORK]: 'No pudimos conectarnos. Revisa tu conexión e inténtalo de nuevo.',
  [FAILURE.UNAUTHORIZED]: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  [FAILURE.FORBIDDEN]: 'No tienes permiso para hacer esto.',
  [FAILURE.NOT_FOUND]: 'No encontramos lo que buscabas.',
  [FAILURE.CONFLICT]: 'Esta acción no es posible en este momento.',
  [FAILURE.VALIDATION]: 'Revisa los datos e inténtalo de nuevo.',
  [FAILURE.SERVER]: 'Algo salió mal de nuestro lado. Ya lo estamos revisando.'
};

export class AgencyError extends Error {
  constructor(kind, message) {
    super(message || FRIENDLY[kind] || FRIENDLY[FAILURE.SERVER]);
    this.name = 'AgencyError';
    this.kind = kind;
  }
}

// Traduce cualquier fallo de axios a un AgencyError. Los 409 y 400 sí
// conservan el texto del backend porque ahí el backend habla en español y
// explica una regla de negocio real ("la solicitud debe estar aprobada antes
// de ejecutarse"), que es justo lo que el usuario necesita leer.
function toAgencyError(error) {
  if (!error?.response) return new AgencyError(FAILURE.NETWORK);
  const { status, data } = error.response;
  const backendMessage = typeof data?.error === 'string' ? data.error : null;
  if (status === 401) return new AgencyError(FAILURE.UNAUTHORIZED);
  if (status === 403) return new AgencyError(FAILURE.FORBIDDEN, backendMessage);
  if (status === 404) return new AgencyError(FAILURE.NOT_FOUND);
  if (status === 409) return new AgencyError(FAILURE.CONFLICT, backendMessage);
  if (status === 400 || status === 422) return new AgencyError(FAILURE.VALIDATION, backendMessage);
  return new AgencyError(FAILURE.SERVER);
}

async function call(promise) {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    throw toAgencyError(error);
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Vocabulario compartido (una sola fuente de verdad para admin y portal)
// ───────────────────────────────────────────────────────────────────────────

// El orden importa: es el orden en que se muestran los filtros y refleja el
// avance real de una solicitud.
export const REQUEST_STATUS = {
  pending: { label: 'Solicitada', tone: 'sky', clientLabel: 'Recibida' },
  reviewing: { label: 'En revisión', tone: 'sky', clientLabel: 'En revisión' },
  approved: { label: 'Aprobada', tone: 'gold', clientLabel: 'Aprobada' },
  in_progress: { label: 'En ejecución', tone: 'grape', clientLabel: 'Trabajando en ello' },
  completed: { label: 'Completada', tone: 'green', clientLabel: 'Lista' },
  returned: { label: 'Devuelta', tone: 'gold', clientLabel: 'Necesitamos un dato' },
  rejected: { label: 'Rechazada', tone: 'magenta', clientLabel: 'No procede' },
  cancelled: { label: 'Cancelada', tone: 'gray', clientLabel: 'Cancelada' }
};

export const REQUEST_STATUS_ORDER = Object.keys(REQUEST_STATUS);

// Estados en los que la solicitud sigue esperando algo de MARKETOS.
export const OPEN_STATUSES = ['pending', 'reviewing', 'approved', 'in_progress', 'returned'];

export const CHANGE_TYPES = [
  { value: 'texto', label: 'Texto' },
  { value: 'imagen', label: 'Imagen' },
  { value: 'precio', label: 'Precio' },
  { value: 'boton', label: 'Botón' },
  { value: 'color', label: 'Color' },
  { value: 'seccion', label: 'Sección' },
  { value: 'formulario', label: 'Formulario' },
  { value: 'producto', label: 'Producto' },
  { value: 'workflow', label: 'Automatización' },
  { value: 'campo_personalizado', label: 'Campo personalizado' },
  { value: 'mensaje_automatico', label: 'Mensaje automático' },
  { value: 'campana', label: 'Campaña' },
  { value: 'pagina_completa', label: 'Página completa' },
  { value: 'otro', label: 'Otro' }
];

export const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Lo necesito pronto' },
  { value: 'urgent', label: 'Es urgente' }
];

export const BILLING_TYPES = [
  { value: 'unico', label: 'Pago único' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'anual', label: 'Anual' },
  { value: 'por_uso', label: 'Por uso' }
];

// Estados de un producto ya contratado por un cliente.
export const SERVICE_STATUS = {
  planned: { label: 'Planificado', tone: 'gray' },
  onboarding: { label: 'En implementación', tone: 'sky' },
  active: { label: 'Activo', tone: 'green' },
  paused: { label: 'Pausado', tone: 'gold' },
  finished: { label: 'Finalizado', tone: 'gray' }
};

// Quién originó una acción en la auditoría (spec §9).
export const ACTOR_LABELS = {
  marketos_admin: 'Administrador MARKETOS',
  mcp_admin: 'MCP',
  employee: 'Empleado',
  client: 'Cliente',
  ai: 'IA',
  mcp: 'MCP'
};

export const EVENT_LABELS = {
  created: 'Solicitud creada',
  proposed: 'Cambio propuesto por IA',
  review_started: 'Puesta en revisión',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  returned_to_client: 'Devuelta al cliente',
  clarified_by_client: 'El cliente respondió',
  cancelled: 'Cancelada',
  cancelled_by_client: 'Cancelada por el cliente',
  assignment_updated: 'Asignación actualizada',
  preview_updated: 'Vista previa actualizada',
  execution_started: 'Ejecución iniciada',
  execution_completed: 'Cambio ejecutado',
  execution_failed: 'La ejecución falló'
};

// ───────────────────────────────────────────────────────────────────────────
// Reglas de negocio expresadas una sola vez
// ───────────────────────────────────────────────────────────────────────────

// Regla central de MARKETOS: nada se ejecuta sin aprobación previa.
// La UI usa esto para deshabilitar el botón; el backend lo vuelve a validar
// en POST /requests/:id/execute — el botón oculto NO es la protección.
export function canExecute(request) {
  return request?.status === 'approved';
}

export function canApprove(request) {
  return ['pending', 'reviewing'].includes(request?.status);
}

export function canReject(request) {
  return !['completed', 'cancelled', 'rejected'].includes(request?.status);
}

export function canReturn(request) {
  return ['pending', 'reviewing'].includes(request?.status);
}

// ───────────────────────────────────────────────────────────────────────────
// Operación MARKETOS (administrador)
// ───────────────────────────────────────────────────────────────────────────

export const adminService = {
  metrics: (clientId) => call(api.get('/api/agency/metrics', { params: clientId ? { clientId } : {} })),

  listClients: () => call(api.get('/api/agency/clients')),
  getClient: (clientId) => call(api.get(`/api/agency/clients/${clientId}`)),
  saveClientProfile: (clientId, profile) => call(api.put(`/api/agency/clients/${clientId}/profile`, profile)),

  listCatalog: () => call(api.get('/api/agency/services/all')),
  createProduct: (product) => call(api.post('/api/agency/services', product)),
  updateProduct: (serviceId, product) => call(api.put(`/api/agency/services/${serviceId}`, product)),
  // "Eliminar" un producto es desactivarlo: los clientes que ya lo tienen
  // contratado deben seguir viendo su nombre en su portal y en la auditoría.
  archiveProduct: (serviceId) => call(api.put(`/api/agency/services/${serviceId}`, { active: false })),

  assignProduct: (clientId, payload) => call(api.post(`/api/agency/clients/${clientId}/services`, payload)),
  updateAssignment: (assignmentId, payload) => call(api.patch(`/api/agency/client-services/${assignmentId}`, payload)),
  listProductionSites: () => call(api.get('/api/agency/web-production/sites')),
  updateWebProject: (projectId, payload) => call(api.patch(`/api/agency/web-projects/${projectId}`, payload)),

  listRequests: (status) => call(api.get('/api/agency/requests', { params: status ? { status } : {} })),
  getRequest: (requestId) => call(api.get(`/api/agency/requests/${requestId}`)),
  review: (requestId) => call(api.post(`/api/agency/requests/${requestId}/review`, {})),
  approve: (requestId, note) => call(api.post(`/api/agency/requests/${requestId}/approve`, { note })),
  approveAndExecute: (requestId, manualSummary) => call(api.post(`/api/agency/requests/${requestId}/approve-and-execute`, {
    note: 'Aprobada y aplicada desde Operación MARKETOS',
    manual_summary: manualSummary
  })),
  reject: (requestId, reason) => call(api.post(`/api/agency/requests/${requestId}/reject`, { reason })),
  returnToClient: (requestId, reason) => call(api.post(`/api/agency/requests/${requestId}/return`, { reason })),
  cancel: (requestId, reason) => call(api.post(`/api/agency/requests/${requestId}/cancel`, { reason })),
  execute: (requestId, manualSummary) => call(api.post(`/api/agency/requests/${requestId}/execute`, { manual_summary: manualSummary })),
  savePreview: (requestId, items, impact) => call(api.put(`/api/agency/requests/${requestId}/preview`, { items, impact })),
  saveAssignment: (requestId, payload) => call(api.patch(`/api/agency/requests/${requestId}/assignment`, payload)),

  listMessages: (clientId) => call(api.get(`/api/agency/clients/${clientId}/messages`)),
  listInboxMessages: () => call(api.get('/api/agency/messages')),
  sendMessage: (clientId, body, requestId) => call(api.post(`/api/agency/clients/${clientId}/messages`, { body, request_id: requestId })),

  audit: (params) => call(api.get('/api/agency/audit', { params })),

  // IA / MCP: proponer nunca ejecuta. Devuelve una solicitud pendiente de
  // aprobación humana, igual que si la hubiera creado un cliente.
  proposeChange: (payload) => call(api.post('/api/agency/commands/propose', payload)),
  getClientContext: (clientId) => call(api.get(`/api/agency/clients/${clientId}/context`)),
  listAiConversations: (clientId) => call(api.get('/api/agency/ai/conversations', { params: { clientId } })),
  getAiConversation: (conversationId) => call(api.get(`/api/agency/ai/conversations/${conversationId}`)),
  chatWithAi: (payload) => call(api.post('/api/agency/ai/chat', payload)),
  notificationSettings: () => call(api.get('/api/agency/notification-settings')),
  saveNotificationSettings: (payload) => call(api.put('/api/agency/notification-settings', payload)),
  testNotifications: () => call(api.post('/api/agency/notification-settings/test', {})),
  guidedAiSettings: () => call(api.get('/api/agency/ai-assistant-settings')),
  saveGuidedAiSettings: (payload) => call(api.put('/api/agency/ai-assistant-settings', payload)),
  testGuidedAiSettings: () => call(api.post('/api/agency/ai-assistant-settings/test', {})),
  disconnectGuidedAiSettings: () => call(api.delete('/api/agency/ai-assistant-settings')),
  listWhatsAppChannels: () => call(api.get('/api/agency/whatsapp-channels')),
  startWhatsAppChannel: (payload) => call(api.post('/api/agency/whatsapp-channels/baileys/start', payload)),
  whatsAppQr: (channelId) => call(api.get(`/api/agency/whatsapp-channels/${channelId}/qr`)),
  reconnectWhatsAppChannel: (channelId) => call(api.post(`/api/agency/whatsapp-channels/${channelId}/reconnect`, {})),
  deleteWhatsAppChannel: (channelId) => call(api.delete(`/api/agency/whatsapp-channels/${channelId}`)),
  mcpListTools: () => call(api.post('/api/mcp', { method: 'tools/list' })),
  mcpCall: (name, args) => call(api.post('/api/mcp', { method: 'tools/call', params: { name, arguments: args } }))
};

// ───────────────────────────────────────────────────────────────────────────
// Mi Proyecto (cliente)
// ───────────────────────────────────────────────────────────────────────────

export const portalService = {
  // Una sola llamada devuelve TODO lo del cliente. El backend filtra por el
  // clientId del JWT, así que no hay forma de pedir los datos de otro.
  summary: () => call(api.get('/api/agency/portal/summary')),
  createRequest: (payload) => call(api.post('/api/agency/portal/requests', payload)),
  listAiIntakes: () => call(api.get('/api/agency/portal/ai-intakes')),
  startAiIntake: (goal, serviceId) => call(api.post('/api/agency/portal/ai-intakes', { goal, service_id: serviceId })),
  submitAiIntake: (intakeId, answers) => call(api.post(`/api/agency/portal/ai-intakes/${intakeId}/submit`, { answers })),
  workflowNotificationRecipient: () => call(api.get('/api/agency/portal/workflow-notification-recipient')),
  saveWorkflowNotificationRecipient: (payload) => call(api.put('/api/agency/portal/workflow-notification-recipient', payload)),
  aiConnection: () => call(api.get('/api/agency/portal/ai-connection')),
  saveAiConnection: (apiKey) => call(api.put('/api/agency/portal/ai-connection', { apiKey })),
  testAiConnection: () => call(api.post('/api/agency/portal/ai-connection/test', {})),
  disconnectAiConnection: () => call(api.delete('/api/agency/portal/ai-connection')),
  cancelRequest: (requestId) => call(api.post(`/api/agency/portal/requests/${requestId}/cancel`, {})),
  reply: (body, requestId) => call(api.post('/api/agency/portal/messages', { body, request_id: requestId }))
};

// ───────────────────────────────────────────────────────────────────────────
// Utilidades de presentación
// ───────────────────────────────────────────────────────────────────────────

export function formatMoney(value) {
  const number = Number(value || 0);
  return `$${number.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// SQLite devuelve tres formas distintas y las tres llegan a estas funciones:
//   '2026-08-17'            → columnas DATE (desired_date, start_date)
//   '2026-08-17 14:30:00'   → columnas DATETIME, en UTC y con espacio
//   '2026-08-17T14:30:00Z'  → ISO, cuando el valor pasó por JavaScript
// Un `new Date()` ingenuo falla con la primera (añadirle 'Z' da fecha
// inválida) y con la segunda (Safari no acepta el espacio). Se normaliza acá,
// una sola vez, en vez de en cada pantalla.
function parseDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  // Fecha sin hora: se construye en horario local para que no se corra un día
  // al mostrarla en Ecuador (UTC-5).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const normalized = text.includes('T') ? text : `${text.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
